import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { CalendarClock, Save } from "lucide-react";
import { updateVzDigestConfig, updateVzDealerTz } from "@/lib/verizonSalesApi";

const DAYS = [[1, "Mon"], [2, "Tue"], [3, "Wed"], [4, "Thu"], [5, "Fri"], [6, "Sat"], [7, "Sun"]];
export const ZONES = [["America/New_York", "Eastern"], ["America/Chicago", "Central"], ["America/Denver", "Mountain"],
  ["America/Phoenix", "Arizona"], ["America/Los_Angeles", "Pacific"], ["America/Anchorage", "Alaska"], ["Pacific/Honolulu", "Hawaii"]];
export const zoneName = (tz) => ZONES.find(([z]) => z === tz)?.[1] || tz;
const TIMES = Array.from({ length: 96 }, (_, i) => {
  const h = Math.floor(i / 4), m = (i % 4) * 15;
  const v = `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
  return [v, `${((h + 11) % 12) + 1}:${String(m).padStart(2, "0")} ${h < 12 ? "AM" : "PM"}`];
});
export const timeLabel = (v) => TIMES.find(([t]) => t === v)?.[1] || v;

export const DigestSchedule = ({ cfg, onSaved }) => {
  const [days, setDays] = useState(cfg.schedule.days);
  const [time, setTime] = useState(cfg.schedule.time);
  const toggle = (d) => setDays((cur) => (cur.includes(d) ? cur.filter((x) => x !== d) : [...cur, d].sort()));
  const save = async () => {
    if (!days.length) return toast.error("Pick at least one day");
    try { onSaved(await updateVzDigestConfig({ schedule: { days, time } })); toast.success("Digest schedule saved"); }
    catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  return (
    <div className="border border-teal/30 bg-teal/5 rounded-lg p-3 space-y-3" data-testid="vz-digest-schedule">
      <div className="text-xs font-semibold text-slate-700 flex items-center gap-1.5"><CalendarClock className="w-4 h-4 text-teal" /> When the digest goes out</div>
      <div className="flex flex-wrap items-center gap-2">
        {DAYS.map(([d, l]) => (
          <button key={d} type="button" onClick={() => toggle(d)} data-testid={`vz-digest-day-${d}`}
            className={`w-12 h-8 rounded-md border text-xs font-medium transition-colors ${days.includes(d) ? "bg-navy text-white border-navy" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}>{l}</button>
        ))}
        <span className="text-xs text-slate-500 mx-1">at</span>
        <Select value={time} onValueChange={setTime}>
          <SelectTrigger className="h-8 w-32 text-xs" data-testid="vz-digest-time-select"><SelectValue /></SelectTrigger>
          <SelectContent className="max-h-72">{TIMES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
        </Select>
        <Button size="sm" onClick={save} data-testid="vz-digest-schedule-save"><Save className="w-4 h-4 mr-1" /> Save schedule</Button>
      </div>
      <p className="text-xs text-slate-500">Each dealer gets it at <b>{timeLabel(cfg.schedule.time)} their own time</b> (from their main store's address, or the time zone picked for that dealer above). Numbers are always month-to-date from the latest DLAR report.</p>
    </div>
  );
};

export const DealerTimezone = ({ dealer, onSaved }) => {
  const save = async (v) => {
    try { await updateVzDealerTz({ dealer_id: dealer.id, timezone: v === "auto" ? null : v }); toast.success("Dealer time zone saved"); onSaved(); }
    catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  return (
    <div className="flex items-center gap-2 text-xs" data-testid="vz-digest-dealer-tz">
      <span className="font-semibold text-slate-600">Time zone:</span>
      <Select value={dealer.timezone_override || "auto"} onValueChange={save}>
        <SelectTrigger className="h-8 w-64 text-xs" data-testid="vz-digest-dealer-tz-select"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="auto">Automatic: {zoneName(dealer.auto_timezone)} (main store)</SelectItem>
          {ZONES.map(([z, l]) => <SelectItem key={z} value={z}>{l}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
};
